import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface CreateEngagementTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firmId: string;
  accessToken: string;
  onCreated: () => void;
  editingType?: any;
}

const CATEGORY_OPTIONS = [
  { value: 'migration', label: 'Migration', description: 'Cloud migrations, platform migrations' },
  { value: 'implementation', label: 'Implementation', description: 'ITSM, JSM, Confluence implementations' },
  { value: 'fast_track', label: 'Fast Track', description: 'Quick discovery & recommendations (2-3 weeks)' },
  { value: 'health_checkup', label: 'Health Checkup', description: 'System audits, tuneups, optimization' },
  { value: 'custom', label: 'Custom', description: 'Other engagement types' },
];

const PRICING_MODELS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'fixed', label: 'Fixed Price' },
  { value: 'hybrid', label: 'Hybrid (T&M with cap)' },
];

export function CreateEngagementTypeDialog({
  open,
  onOpenChange,
  firmId,
  accessToken,
  onCreated,
  editingType,
}: CreateEngagementTypeDialogProps) {
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState(editingType?.name || '');
  const [description, setDescription] = useState(editingType?.description || '');
  const [category, setCategory] = useState(editingType?.category || 'implementation');
  const [defaultScope, setDefaultScope] = useState(editingType?.defaultScope || '');
  const [deliverables, setDeliverables] = useState<string[]>(editingType?.defaultDeliverables || []);
  const [newDeliverable, setNewDeliverable] = useState('');
  const [standardDurationWeeks, setStandardDurationWeeks] = useState(editingType?.standardDurationWeeks?.toString() || '');
  const [estimatedHours, setEstimatedHours] = useState(editingType?.estimatedHours?.toString() || '');
  const [pricingModel, setPricingModel] = useState(editingType?.pricingModel || 'hourly');
  const [defaultHourlyRate, setDefaultHourlyRate] = useState(editingType?.defaultHourlyRate?.toString() || '150');
  const [defaultFixedPrice, setDefaultFixedPrice] = useState(editingType?.defaultFixedPrice?.toString() || '');
  const [assumptions, setAssumptions] = useState<string[]>(editingType?.assumptions || []);
  const [newAssumption, setNewAssumption] = useState('');
  const [exclusions, setExclusions] = useState<string[]>(editingType?.exclusions || []);
  const [newExclusion, setNewExclusion] = useState('');

  const handleAddDeliverable = () => {
    if (newDeliverable.trim()) {
      setDeliverables([...deliverables, newDeliverable.trim()]);
      setNewDeliverable('');
    }
  };

  const handleRemoveDeliverable = (index: number) => {
    setDeliverables(deliverables.filter((_, i) => i !== index));
  };

  const handleAddAssumption = () => {
    if (newAssumption.trim()) {
      setAssumptions([...assumptions, newAssumption.trim()]);
      setNewAssumption('');
    }
  };

  const handleRemoveAssumption = (index: number) => {
    setAssumptions(assumptions.filter((_, i) => i !== index));
  };

  const handleAddExclusion = () => {
    if (newExclusion.trim()) {
      setExclusions([...exclusions, newExclusion.trim()]);
      setNewExclusion('');
    }
  };

  const handleRemoveExclusion = (index: number) => {
    setExclusions(exclusions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validation
      if (!name.trim()) {
        toast.error('Please enter a name');
        return;
      }

      if (!category) {
        toast.error('Please select a category');
        return;
      }

      if (pricingModel === 'hourly' && !defaultHourlyRate) {
        toast.error('Please enter a default hourly rate');
        return;
      }

      if (pricingModel === 'fixed' && !defaultFixedPrice) {
        toast.error('Please enter a default fixed price');
        return;
      }

      const payload = {
        name: name.trim(),
        description: description.trim(),
        category,
        defaultScope: defaultScope.trim(),
        defaultDeliverables: deliverables,
        standardDurationWeeks: standardDurationWeeks ? parseInt(standardDurationWeeks) : 0,
        estimatedHours: estimatedHours ? parseInt(estimatedHours) : 0,
        pricingModel,
        defaultHourlyRate: pricingModel === 'hourly' || pricingModel === 'hybrid' 
          ? parseFloat(defaultHourlyRate) 
          : undefined,
        defaultFixedPrice: pricingModel === 'fixed' || pricingModel === 'hybrid'
          ? parseFloat(defaultFixedPrice)
          : undefined,
        assumptions,
        exclusions,
        isActive: true,
      };

      const url = editingType
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types/${editingType.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`;

      const method = editingType ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save engagement type');
      }

      toast.success(editingType ? 'Engagement type updated' : 'Engagement type created');
      onCreated();
      onOpenChange(false);
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error saving engagement type:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save engagement type');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setCategory('implementation');
    setDefaultScope('');
    setDeliverables([]);
    setNewDeliverable('');
    setStandardDurationWeeks('');
    setEstimatedHours('');
    setPricingModel('hourly');
    setDefaultHourlyRate('150');
    setDefaultFixedPrice('');
    setAssumptions([]);
    setNewAssumption('');
    setExclusions([]);
    setNewExclusion('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingType ? 'Edit Engagement Type' : 'Create New Engagement Type'}
          </DialogTitle>
          <DialogDescription>
            Define a reusable engagement template for your consulting services
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Engagement Name *</Label>
              <Input
                id="name"
                placeholder="e.g., ITSM Implementation, Fast Track Discovery"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this engagement type"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-muted-foreground">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* SOW Template Defaults */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">SOW Template Defaults</h3>
            
            <div>
              <Label htmlFor="scope">Default Scope of Work</Label>
              <Textarea
                id="scope"
                placeholder="Standard scope description that will pre-populate SOWs"
                value={defaultScope}
                onChange={(e) => setDefaultScope(e.target.value)}
                rows={4}
              />
            </div>

            <div>
              <Label>Default Deliverables</Label>
              <div className="space-y-2">
                {deliverables.map((deliverable, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="secondary" className="flex-1">
                      {deliverable}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveDeliverable(index)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a deliverable"
                    value={newDeliverable}
                    onChange={(e) => setNewDeliverable(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDeliverable()}
                  />
                  <Button onClick={handleAddDeliverable} variant="outline">
                    <Plus className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline & Effort */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Timeline & Effort</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Standard Duration (weeks)</Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="e.g., 12"
                  value={standardDurationWeeks}
                  onChange={(e) => setStandardDurationWeeks(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="hours">Estimated Hours</Label>
                <Input
                  id="hours"
                  type="number"
                  placeholder="e.g., 480"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Pricing</h3>
            
            <div>
              <Label htmlFor="pricing-model">Pricing Model *</Label>
              <Select value={pricingModel} onValueChange={setPricingModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_MODELS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(pricingModel === 'hourly' || pricingModel === 'hybrid') && (
              <div>
                <Label htmlFor="hourly-rate">Default Hourly Rate ($) *</Label>
                <Input
                  id="hourly-rate"
                  type="number"
                  placeholder="150"
                  value={defaultHourlyRate}
                  onChange={(e) => setDefaultHourlyRate(e.target.value)}
                />
              </div>
            )}

            {(pricingModel === 'fixed' || pricingModel === 'hybrid') && (
              <div>
                <Label htmlFor="fixed-price">Default Fixed Price ($) {pricingModel === 'hybrid' ? '(Cap)' : '*'}</Label>
                <Input
                  id="fixed-price"
                  type="number"
                  placeholder="50000"
                  value={defaultFixedPrice}
                  onChange={(e) => setDefaultFixedPrice(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Assumptions */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Standard Assumptions</h3>
            
            <div className="space-y-2">
              {assumptions.map((assumption, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Badge variant="outline" className="flex-1 text-left whitespace-normal py-2">
                    {assumption}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAssumption(index)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Add an assumption"
                  value={newAssumption}
                  onChange={(e) => setNewAssumption(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAssumption()}
                />
                <Button onClick={handleAddAssumption} variant="outline">
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Exclusions */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Standard Exclusions</h3>
            
            <div className="space-y-2">
              {exclusions.map((exclusion, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Badge variant="outline" className="flex-1 text-left whitespace-normal py-2">
                    {exclusion}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveExclusion(index)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Add an exclusion"
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddExclusion()}
                />
                <Button onClick={handleAddExclusion} variant="outline">
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
            {editingType ? 'Update' : 'Create'} Engagement Type
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}